import React from 'react'

import styled, { css } from 'styled-components'

import { Heading, Icon, IconEnum, IconSizeEnum, Paragraph } from '@atoms/index'
import { PlansBoxShadow, ResponsivePXValue, theme } from '@components/Theme'
import { CheckoutStepsEnum, useGetAppQuery } from '@hooks/api'
import { DeviceTypeEnum } from '@uctypes/api/globalTypes'
import { CheckoutStepperProps, StepConfig } from '@utility/CheckoutPure'
import { checkoutLogger } from '@utility/Logger'

const Container = styled.div<{ $isNativeApplication: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: fixed;
  z-index: 11;

  .step-number-desktop {
    padding-top: 4px;
  }

  background-color: ${({ theme }) => theme.colors.whites.wildSand};
  ${ResponsivePXValue('padding-top', { mobile: '16px', tablet: '16px', desktop: '0' })}
  ${ResponsivePXValue('margin-top', { mobile: '0', tablet: '-8px', desktop: '0' })}
  ${ResponsivePXValue('top', { mobile: '56px', tablet: '64px', desktop: '64px' })}
  ${({ $isNativeApplication }) => $isNativeApplication && ResponsivePXValue('top', { mobile: '0px', tablet: '64px', desktop: '64px' })}
`

const StepperContainer = styled.nav<{ $isMobile: boolean }>`
  background-color: ${({ theme }) => theme.colors.oranges.roseBud}; 
  
 
  ${ResponsivePXValue('max-width', { mobile: '90vw', tablet: '95vw', desktop: '1136px' })}
  ${ResponsivePXValue('width', { mobile: '90vw', tablet: '95vw', desktop: '1136px' })}
  ${ResponsivePXValue('height', { mobile: 'fit-content', tablet: 'auto', desktop: 'auto' })}
  ${ResponsivePXValue('margin', { mobile: '0', tablet: '0 0 0 0', desktop: '16px 0 0 0' })}
  ${ResponsivePXValue('border-radius', { mobile: '50px', tablet: '8px', desktop: '8px' })}
  ${PlansBoxShadow}

  // Apply border only on desktop
  ${({ $isMobile, theme }) => !$isMobile && css`
    border: 1px solid ${theme.colors.oranges.coral};
  `}

    ${({ $isMobile, theme }) => $isMobile && css`
    border: 1px solid ${theme.colors.whites.pureWhite};
  `}
`

const StepperInnerContainer = styled.div`
  position: relative;
  height: 100%;

  ${ResponsivePXValue('padding', { mobile: '0 0', tablet: '16px', desktop: '16px' })}

  // Class for step labels used by Paragraph component
  .step-label {
    text-align: center;
    ${ResponsivePXValue('width', { mobile: '124px', tablet: '72px', desktop: '150px' })} 
  }

  .step-number-label {
   padding-top: 6px;
  }
`

// --- Desktop Styles ---

const DesktopStepperView = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start; 
  justify-content: space-between;
`

const DesktopStepItem = styled.div<{ $isClickable?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  flex: 1 1 0; 
  min-width: 0; 
  cursor: ${({ $isClickable }) => $isClickable ? 'pointer' : 'default'};
`

const StepIndicatorWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center; 
  width: 100%; 
  justify-content: center; 
  ${ResponsivePXValue('margin-bottom', '8px')}
`

const StepIndicator = styled.div<{ $isActive?: boolean, $isCompleted?: boolean }>`
  width: 36px;  
  height: 36px;
  border-radius: 50%; // Circle shape
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  z-index: 1; // Above connector
  transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
  background-color: ${({ theme }) => theme.colors.whites.pureWhite};

  ${({ theme, $isActive, $isCompleted }) => {
    const activeColor = theme.colors.oranges.coral
    const upcomingBorderColor = theme.colors.slates.celeste
    const textColorActive = activeColor
    const textColorUpcoming = theme.colors.slates.celeste

    // Completed State: Filled background, white checkmark
    if ($isCompleted) {
      return css`
        background-color: ${activeColor};
        border: 2px solid ${activeColor}; 
        color: ${theme.colors.whites.pureWhite}; // For checkmark color prop
      `
    }
    // Active State: White background, colored border and text number
    if ($isActive) {
      return css`
        border: 2px solid ${activeColor}; 
        color: ${textColorActive};
        background-color: ${theme.colors.whites.pureWhite}; // Ensure background is white
      `
    }
    // Upcoming state: White background, grey border and text number
    return css`
      border: 2px solid ${upcomingBorderColor}; 
      color: ${textColorUpcoming}; 
      background-color: ${theme.colors.whites.pureWhite}; // Ensure background is white
    `
  }}
`

const StepConnector = styled.div<{ $isCompleted?: boolean }>`
  position: absolute;
  top: 18px; // Vertically center with indicator (height 36px / 2)
  left: calc(50% + 18px); // Adjust start/end for circle radius (36px / 2)
  right: calc(-50% + 18px); 
  height: 2px; // Line thickness
  z-index: 0; // Behind indicator
  // Overflow hidden not strictly needed if ::before is contained, but harmless
  overflow: hidden; 

  // Use pseudo-element for the line style
  &::before { 
    content: '';
    position: absolute;
    top: 0; 
    left: 0; 
    width: 100%; 
    height: 100%;
    transition: background-color 0.3s ease, background-image 0.3s ease; // Add transition
    
    ${({ theme, $isCompleted }) => {
    if ($isCompleted) {
      // --- COMPLETED STATE: Solid orange line ---
      return css`
          background-image: none; // Ensure no gradient is applied
          background-color: ${theme.colors.oranges.coral}; // Set solid color
        `
    } else {
      // --- UPCOMING STATE: Dotted grey line ---
      return css`
          background-color: transparent; // Ensure no solid bg color interferes
          // Apply gradient for dotted effect
          background-image: linear-gradient( 
            to right, 
            ${theme.colors.slates.celeste} 50%, // Dot color
            transparent 50% // Gap color
          );
          background-size: 8px 2px; // Size of dot+gap pattern
          background-repeat: repeat-x;
        `
    }
  }}
  }
`

// --- Mobile Styles ---

const MobileStepperView = styled.div`
  display: flex; 
  align-items: center; 
  justify-content: center;
  height: 90px;
  ${ResponsivePXValue('gap', '12px')}
  padding-top: 2px;
  margin-left: 68px;
`

// *** MobileCircularProgress: Updated size to 90x90px ***
const MobileCircularProgress = styled.div<{
  $completedPercent: number
  $activeSegmentEndPercent: number
}>`
  width: 90px; 
  height: 90px; 
  border-radius: 50%;
  display: flex; 
  align-items: center; 
  justify-content: center;
  flex-shrink: 0; 
  overflow: hidden; 
  position: absolute;
  inset: 0;
  
  // Outer orange border 
  border: 1px solid ${({ theme }) => theme.colors.oranges.coral}; 
  // Base white background
  background-color: ${({ theme }) => theme.colors.whites.pureWhite};

  // Progress Arc Gradient Layer (::before) - gradient logic unchanged
  &::before {
    content: '';
    position: absolute;
    inset: 0; 
    border-radius: 50%; 
    z-index: 1; 

    background: conic-gradient(
      ${({ theme }) => theme.colors.oranges.coral} 0% ${({ $completedPercent }) => $completedPercent}%, 
      ${({ theme }) => theme.colors.oranges.peach} ${({ $completedPercent }) => $completedPercent}% ${({ $activeSegmentEndPercent }) => $activeSegmentEndPercent}%, 
      ${({ theme }) => theme.colors.whites.pureWhite} ${({ $activeSegmentEndPercent }) => $activeSegmentEndPercent}% 100%
    );
  }

  // Center White Overlay
  &::after {
    content: '';
    position: absolute;
    width: 66px; 
    height: 66px; 
    border-radius: 50%;
    z-index: 2; // Above gradient layer
    background-color: ${({ theme }) => theme.colors.whites.pureWhite};
    border: 1px solid ${({ theme }) => theme.colors.oranges.coral}; 
  }
`

const MobileStepCounter = styled.span`
  font-weight: bold;
  z-index: 3; // Above center overlay
  color: ${({ theme }) => theme.colors.greys.mineshaft}; 
  ${ResponsivePXValue('font-size', '14px')}
`

// Utility to get step count, excluding SELECT_MEAL_KITS for mobile
function getMobileStepCount(steps: StepConfig[], isMobile: boolean): number {
  if (!isMobile) return steps.length
  return steps.filter(step => step.id !== 'SELECT_MEAL_KITS').length
}

export const CheckoutStepper = React.memo(function CheckoutStepper({
  steps,
  currentStepIndex,
  maxCompletedStepIndex,
  testModeEnabled = false,
  forceCompletedStepIndexes = [],
  categoryId,
}: CheckoutStepperProps) {

  const { data: appData } = useGetAppQuery()
  const isMobile = appData?.app?.deviceType === DeviceTypeEnum.MOBILE
  const isNativeApplication = appData?.app?.isNativeApplication ?? false

  // Use the correct step count for mobile (exclude SELECT_MEAL_KITS)
  const mobileTotalSteps = getMobileStepCount(steps, isMobile)
  const mobileCurrentStepNumber = Math.min(currentStepIndex + 1, mobileTotalSteps)

  // Basic validation for the steps prop
  if (!steps || !Array.isArray(steps) || steps.length === 0) {
    checkoutLogger.error('CheckoutStepper: steps array is missing, not an array, or empty', { steps })
    return null
  }
  if (currentStepIndex < 0 || currentStepIndex >= steps.length) {
    checkoutLogger.error('CheckoutStepper: currentStepIndex out of bounds', { currentStepIndex, steps })
    return null
  }
  // Validate each step config
  steps.forEach((step, idx) => {
    if (!step.id || !step.label) {
      checkoutLogger.error('CheckoutStepper: step config missing required fields', { step, idx })
    }
  })

  // Calculate percentages for mobile dual-tone conic gradient based on the correct step count
  const numSteps = isMobile ? mobileTotalSteps : steps.length

  // --- MOBILE SPECIAL LOGIC FOR PICK_PLAN ---
  let completedPercent = 0
  let activeSegmentEndPercent = 0

  if (isMobile && steps[currentStepIndex]?.id === CheckoutStepsEnum.PICK_PLAN) {
    // If on "Pick a Plan" and a plan has been selected, advance the *completed* progress
    // to fill the current step, and show the next step as active.
    if (categoryId) {
      completedPercent = 40 // Visually completes step 2 (2/5)
      activeSegmentEndPercent = 60 // Shows step 3 (3/5) as the active segment
    } else {
      // Before plan selection, show progress for the current step
      completedPercent = 20 // Shows step 1 as complete
      activeSegmentEndPercent = 40 // Shows step 2 as active
    }
  } else {
    // Default progress calculation for all other steps
    completedPercent = numSteps > 0 ? (currentStepIndex / numSteps) * 100 : 0
    activeSegmentEndPercent = numSteps > 0 ? (Math.min(currentStepIndex + 1, numSteps) / numSteps) * 100 : 0
  }

  return (
    <Container $isNativeApplication={isNativeApplication}>
      <StepperContainer $isMobile={isMobile}>
        <StepperInnerContainer>
          <Choose>
            <When condition={isMobile}>
              {/* --- Mobile View --- */}
              <MobileStepperView>
              <MobileCircularProgress
                $completedPercent={completedPercent}
                $activeSegmentEndPercent={activeSegmentEndPercent}>
                <MobileStepCounter>
                  <Heading variant='h5' className='step-number-label'>
                    <Choose>
                      {/* On mobile, if on the plan step and a plan is selected, show the next step number as a placeholder */}
                      <When
                        condition={isMobile && steps[currentStepIndex]?.id === CheckoutStepsEnum.PICK_PLAN && !!categoryId}>
                        {`3/${mobileTotalSteps}`}
                      </When>
                      <When condition={!!steps[currentStepIndex]?.stepIcons && mobileCurrentStepNumber === mobileTotalSteps}>
                        <Choose>
                          <When condition={currentStepIndex < maxCompletedStepIndex || forceCompletedStepIndexes.includes(currentStepIndex)}>
                            {/* Use completed icon if provided */}
                            <Choose>
                              <When condition={!!steps[currentStepIndex]?.stepIcons?.completed}>
                                <Icon icon={steps[currentStepIndex]?.stepIcons?.completed} size={IconSizeEnum.MEDIUM} color={theme.colors.oranges.coral} />
                              </When>
                              <Otherwise>
                                <Icon icon={IconEnum.CHECKMARK_SHARP} size={IconSizeEnum.MEDIUM} color={theme.colors.oranges.coral} />
                              </Otherwise>
                            </Choose>
                          </When>
                          <When condition={currentStepIndex === maxCompletedStepIndex}>
                            <Icon icon={steps[currentStepIndex]?.stepIcons?.inProgress} size={IconSizeEnum.MEDIUM} color={theme.colors.oranges.coral} />
                          </When>
                          <Otherwise>
                            <Icon icon={steps[currentStepIndex]?.stepIcons?.upcoming} size={IconSizeEnum.MEDIUM} color={theme.colors.slates.celeste} />
                          </Otherwise>
                        </Choose>
                      </When>
                      <Otherwise>
                        {`${mobileCurrentStepNumber}/${mobileTotalSteps}`}
                      </Otherwise>
                    </Choose>
                  </Heading>
                </MobileStepCounter>
              </MobileCircularProgress>
              <Heading variant='h6' className='step-label'>
                {/* On mobile, if on the plan step and a plan is selected, show the next step label as a placeholder */}
                <Choose>
                  <When condition={
                    isMobile &&
                    steps[currentStepIndex]?.id === CheckoutStepsEnum.PICK_PLAN &&
                    !!categoryId &&
                    currentStepIndex + 1 < steps.length
                  }>
                    {steps[currentStepIndex + 1]?.label || ''}
                  </When>
                  <Otherwise>
                    {steps[currentStepIndex]?.label || ''}
                  </Otherwise>
                </Choose>
              </Heading>
            </MobileStepperView>
          </When>
          <Otherwise>
            {/* --- Desktop View --- */}
            <DesktopStepperView>
              {/* Map over the provided (filtered) steps */}
              {steps.map((step, index) => {
                const isActive = index === currentStepIndex
                const isCompleted = (index < currentStepIndex) || (forceCompletedStepIndexes.includes(index))
                const isClickable = testModeEnabled || (index <= maxCompletedStepIndex)
                const iconConfig = step.stepIcons

                return (
                  <DesktopStepItem
                    key={step.id}
                    // onClick={() => handleStepClick(index)}
                    $isClickable={isClickable}
                    aria-current={isActive ? 'step' : undefined}>
                    <StepIndicatorWrapper>
                      <StepIndicator
                        $isActive={isActive}
                        $isCompleted={isCompleted}>
                        <Choose>
                          <When condition={isCompleted}>
                            {/* Use completed icon if provided */}
                            <Choose>
                              <When condition={!!iconConfig?.completed}>
                                <Icon icon={iconConfig.completed} size={IconSizeEnum.SMALL} color={theme.colors.whites.pureWhite} />
                              </When>
                              <Otherwise>
                                <Icon
                                  icon={IconEnum.CHECKMARK_SHARP}
                                  size={IconSizeEnum.SMALL}
                                  color={theme.colors.whites.pureWhite} />
                              </Otherwise>
                            </Choose>
                          </When>
                          <When condition={!!iconConfig && (index === steps.length - 1)}>
                            {/* Only apply icon logic for steps with icon config (initially last step) */}
                            <Choose>
                              <When condition={isActive}>
                                {/* In-progress: use inProgress icon */}
                                <Icon icon={iconConfig.inProgress} size={IconSizeEnum.SMALL} color={theme.colors.oranges.coral} />
                              </When>
                              <Otherwise>
                                {/* Upcoming: use upcoming icon */}
                                <Icon icon={iconConfig.upcoming} size={IconSizeEnum.SMALL} color={theme.colors.slates.celeste} />
                              </Otherwise>
                            </Choose>
                          </When>
                          <Otherwise>
                            {/* Display the step number relative to the current flow */}
                            <Paragraph variant='p4' className='step-number-desktop' bold
                              color={isActive ? theme.colors.oranges.coral : theme.colors.slates.celeste}>
                              {index + 1}
                            </Paragraph>
                          </Otherwise>
                        </Choose>
                      </StepIndicator>
                      {/* Connector logic remains the same relative to the mapped steps */}
                      {index < steps.length - 1 && (
                        <StepConnector $isCompleted={isCompleted} />
                      )}
                    </StepIndicatorWrapper>
                    <Paragraph variant='p4' bold className='step-label'>
                      {step.label}
                    </Paragraph>
                  </DesktopStepItem>
                )
              })}
            </DesktopStepperView>
          </Otherwise>
        </Choose>
        </StepperInnerContainer>
      </StepperContainer>
    </Container>
  )
})
